import {
  bearingDeg,
  distance,
  nearestPointOnShape,
  normalizeDeg,
  pointInShape,
  signedDistanceToShape,
} from '@sw/geo'
import type {
  Bus,
  DuckingRule,
  Emitter,
  Pose,
  Region,
  Vec2,
  WorldDefinition,
} from '@sw/schema'
import { dbToLinear, directivityGain, distanceGain, dopplerRate } from './attenuation.js'

export interface MixWorld {
  emitters: readonly Emitter[]
  regions: ReadonlyMap<string, Region>
  buses: ReadonlyMap<string, Bus>
  ducking: readonly DuckingRule[]
  masterGain: number
  /** Places where the world should get out of the listener's ears. */
  safetyZones: readonly { regionId: string; duckDb: number }[]
}

export function prepareMix(definition: WorldDefinition): MixWorld {
  return {
    emitters: definition.emitters,
    regions: new Map(definition.regions.map((region) => [region.id, region])),
    buses: new Map(definition.buses.map((bus) => [bus.id, bus])),
    ducking: definition.ducking,
    masterGain: dbToLinear(definition.masterGainDb),
    safetyZones: definition.safetyZones.map((zone) => ({
      regionId: zone.regionId,
      duckDb: zone.duckDb,
    })),
  }
}

export interface ListenerState {
  pose: Pose
  /** Facing, in degrees clockwise from the world's +Y axis. */
  headingDeg: number
  velocityMps?: Vec2
  /** Per-listener bus trims, from the player's own faders. */
  busTrimDb?: Readonly<Record<string, number>>
}

export interface EmitterRuntime {
  /** True when the host wants this sounding — set by rules, not by geometry. */
  playing: boolean
  /** Live position for emitters that move; paths supply one every frame. */
  position?: Vec2
  velocityMps?: Vec2
}

export type Spatialization = 'positional' | 'ambient' | 'nonDiegetic'

export interface VoiceDecision {
  emitterId: string
  busId: string
  /** Final linear gain, after distance, directivity, bus and ducking. */
  gain: number
  /** Degrees from straight ahead: negative to the left, positive to the right. */
  azimuthDeg: number
  distanceM: number
  playbackRate: number
  spatialization: Spatialization
  /**
   * How enveloping the source should be, 0 (a point in front of you) to 1
   * (all around you). Zones cross this over as you step inside, so arriving in
   * a courtyard feels like arriving rather than like a switch being thrown.
   */
  ambientBlend: number
  /** False when culled: the host keeps the playhead running silently. */
  audible: boolean
  priority: number
}

export interface MixDecision {
  voices: VoiceDecision[]
  /** True while the listener is somewhere the world has been told to hush. */
  inSafetyZone: boolean
  /** Target bus gains. The host smooths towards these over the duck times. */
  busGains: Record<string, number>
  /** Emitters that just lost or regained a voice, for diagnostics. */
  virtualized: string[]
  restored: string[]
}

export interface MixState {
  audible: Set<string>
}

export function createMixState(): MixState {
  return { audible: new Set() }
}

export interface MixOptions {
  /** How many sources are actually rendered. The rest keep time silently. */
  maxVoices?: number
  /** Gains below this are treated as silence. */
  silenceThreshold?: number
}

const DEFAULT_MAX_VOICES = 24
const DEFAULT_SILENCE_THRESHOLD = dbToLinear(-60)

interface Placed {
  emitter: Emitter
  spatialization: Spatialization
  /** Where the sound is, for positional voices. */
  position?: Vec2
  distanceM: number
  ambientBlend: number
  geometryGain: number
  velocityMps?: Vec2
}

function placeEmitter(
  world: MixWorld,
  listener: ListenerState,
  emitter: Emitter,
  runtime: EmitterRuntime,
): Placed | undefined {
  const listenerPosition = listener.pose.position

  switch (emitter.kind) {
    case 'bed':
      return {
        emitter,
        spatialization: 'nonDiegetic',
        distanceM: 0,
        ambientBlend: 1,
        geometryGain: 1,
      }

    case 'point': {
      const position = runtime.position ?? emitter.position
      const away = distance(listenerPosition, position)
      return {
        emitter,
        spatialization: 'positional',
        position,
        distanceM: away,
        ambientBlend: 0,
        geometryGain: distanceGain(away, emitter.distance),
        velocityMps: runtime.velocityMps,
      }
    }

    case 'path': {
      // A path only has a position while it is in flight; the host supplies it.
      const position = runtime.position
      if (!position) return undefined
      const away = distance(listenerPosition, position)
      return {
        emitter,
        spatialization: 'positional',
        position,
        distanceM: away,
        ambientBlend: 0,
        geometryGain: distanceGain(away, emitter.distance),
        velocityMps: runtime.velocityMps,
      }
    }

    case 'companion': {
      // Offsets are in the listener's own frame, so they turn as the listener does.
      const heading = (listener.headingDeg * Math.PI) / 180
      const forward = { x: Math.sin(heading), y: Math.cos(heading) }
      const right = { x: forward.y, y: -forward.x }
      const position = {
        x: listenerPosition.x + forward.x * emitter.offset.forwardM + right.x * emitter.offset.rightM,
        y: listenerPosition.y + forward.y * emitter.offset.forwardM + right.y * emitter.offset.rightM,
      }
      const away = distance(listenerPosition, position)
      return {
        emitter,
        spatialization: 'positional',
        position,
        distanceM: away,
        ambientBlend: 0,
        geometryGain: distanceGain(away, emitter.distance),
      }
    }

    case 'zone': {
      const region = world.regions.get(emitter.regionId)
      if (!region) return undefined

      const signed = signedDistanceToShape(listenerPosition, region.shape)
      const outsideBy = Math.max(0, signed)
      // Deep inside, the layer is all around you; at the boundary it has
      // collapsed to a direction. `signed` is negative within the region.
      const ambientBlend = Math.min(1, Math.max(0, -signed / Math.max(emitter.transitionWidthM, 1e-6)))
      const position = nearestPointOnShape(listenerPosition, region.shape)

      return {
        emitter,
        spatialization: ambientBlend >= 1 ? 'ambient' : 'positional',
        position,
        distanceM: outsideBy,
        ambientBlend,
        geometryGain: distanceGain(outsideBy, emitter.distance),
      }
    }
  }
}

function relativeAzimuth(listener: ListenerState, source: Vec2): number {
  const absolute = bearingDeg(listener.pose.position, source)
  const relative = normalizeDeg(absolute - listener.headingDeg)
  return relative > 180 ? relative - 360 : relative
}

function radialSpeed(listener: ListenerState, placed: Placed): number {
  if (!placed.position || !placed.velocityMps) return 0
  const dx = placed.position.x - listener.pose.position.x
  const dy = placed.position.y - listener.pose.position.y
  const away = Math.hypot(dx, dy)
  if (away === 0) return 0

  const relativeX = placed.velocityMps.x - (listener.velocityMps?.x ?? 0)
  const relativeY = placed.velocityMps.y - (listener.velocityMps?.y ?? 0)
  return (relativeX * dx + relativeY * dy) / away
}

/**
 * Decides the whole mix for one frame: what is audible, from which direction,
 * how loud, and at what rate.
 *
 * Deliberately makes no sound. Given a world, a listener and what the rules
 * have asked to play, it returns numbers — which means the trickiest part of
 * the product can be asserted in a test with no audio device, in CI, and
 * replayed from a recorded trace to the same answer.
 */
export function computeMix(
  world: MixWorld,
  state: MixState,
  listener: ListenerState,
  runtimes: ReadonlyMap<string, EmitterRuntime>,
  options: MixOptions = {},
): MixDecision {
  const maxVoices = options.maxVoices ?? DEFAULT_MAX_VOICES
  const silence = options.silenceThreshold ?? DEFAULT_SILENCE_THRESHOLD

  const placed: Placed[] = []
  for (const emitter of world.emitters) {
    const runtime = runtimes.get(emitter.id)
    if (!runtime?.playing) continue
    const result = placeEmitter(world, listener, emitter, runtime)
    if (result) placed.push(result)
  }

  // Bus levels first: ducking depends on what is sounding, and every voice
  // then reads its bus once.
  const rawBusGain = new Map<string, number>()
  for (const [busId, bus] of world.buses) {
    const trim = listener.busTrimDb?.[busId] ?? 0
    rawBusGain.set(busId, dbToLinear(bus.gainDb + trim))
  }

  const busActivity = new Map<string, number>()
  for (const item of placed) {
    const contribution = item.geometryGain * dbToLinear(item.emitter.gainDb)
    busActivity.set(
      item.emitter.busId,
      Math.max(busActivity.get(item.emitter.busId) ?? 0, contribution),
    )
  }

  // A world that sends people across roads has to be able to get out of
  // their ears. Applied to every bus at once rather than through the ducking
  // matrix, because this is not a mix decision — it is a safety one.
  let safetyDuckDb = 0
  for (const zone of world.safetyZones) {
    const region = world.regions.get(zone.regionId)
    if (!region) continue
    if (pointInShape(listener.pose.position, region.shape)) {
      safetyDuckDb = Math.max(safetyDuckDb, zone.duckDb)
    }
  }
  const safetyGain = dbToLinear(-safetyDuckDb)

  const busGains: Record<string, number> = {}
  for (const [busId, gain] of rawBusGain) {
    let duckDb = 0
    for (const rule of world.ducking) {
      if (rule.targetBusId !== busId) continue
      if ((busActivity.get(rule.sourceBusId) ?? 0) <= silence) continue
      // Several sources ducking the same bus should not stack into silence.
      duckDb = Math.max(duckDb, rule.amountDb)
    }
    busGains[busId] = gain * dbToLinear(-duckDb) * safetyGain
  }

  const voices: VoiceDecision[] = []
  for (const item of placed) {
    const busGain = busGains[item.emitter.busId] ?? 1
    const gain =
      item.geometryGain * dbToLinear(item.emitter.gainDb) * busGain * world.masterGain

    let azimuthDeg = 0
    let directivity = 1
    if (item.spatialization === 'positional' && item.position) {
      azimuthDeg = relativeAzimuth(listener, item.position)
      if (item.emitter.kind === 'point' && item.emitter.directivity) {
        const sourceToListener = bearingDeg(item.position, listener.pose.position)
        directivity = directivityGain(sourceToListener, item.emitter.directivity)
      }
    }

    const useDoppler = item.emitter.kind === 'path' && item.emitter.doppler
    voices.push({
      emitterId: item.emitter.id,
      busId: item.emitter.busId,
      gain: gain * directivity,
      azimuthDeg,
      distanceM: item.distanceM,
      playbackRate: useDoppler ? dopplerRate(radialSpeed(listener, item)) : 1,
      spatialization: item.spatialization,
      ambientBlend: item.ambientBlend,
      audible: true,
      priority: item.emitter.priority,
    })
  }

  // Cull by what the listener would actually notice. Priority breaks ties so a
  // guide's voice survives a swell of ambience rather than being crowded out.
  const ranked = [...voices].sort(
    (a, b) => b.gain * (1 + b.priority / 100) - a.gain * (1 + a.priority / 100),
  )
  const keep = new Set(
    ranked.filter((voice) => voice.gain > silence).slice(0, maxVoices).map((v) => v.emitterId),
  )

  const virtualized: string[] = []
  const restored: string[] = []
  for (const voice of voices) {
    voice.audible = keep.has(voice.emitterId)
    if (voice.audible && !state.audible.has(voice.emitterId)) restored.push(voice.emitterId)
    if (!voice.audible && state.audible.has(voice.emitterId)) virtualized.push(voice.emitterId)
  }
  for (const emitterId of state.audible) {
    if (!voices.some((voice) => voice.emitterId === emitterId)) virtualized.push(emitterId)
  }
  state.audible = keep

  return { voices, busGains, virtualized, restored, inSafetyZone: safetyDuckDb > 0 }
}
